import { AlertDialog } from '@scholastic/mockingjay';
import { useCallback, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { extendSession, signOut } from '@/services/auth/auth-service';
import { endAppSession } from '@/services/app-session/end-app-session';
import { useExtendSession } from '@/hooks/use-extend-session/use-extend-session';

export interface IdleAlertDialogProps {
  /** Starts a fresh app session when the user chooses to stay signed in. */
  onStaySignedIn?: () => void | Promise<void>;
}

export function IdleAlertDialog({ onStaySignedIn }: IdleAlertDialogProps = {}) {
  const navigate = useNavigate();
  const [idleDialogOpen, setIdleDialogOpen] = useState(false);

  const IDLE_TIMEOUT = 1000 * 60 * 60 * 8; // 8 hours
  const PROMPT_TIME = 1000 * 60 * 60 * 7.5; // 7.5 hours
  const promptTimeThreshold = IDLE_TIMEOUT - PROMPT_TIME;
  const countdownDuration = IDLE_TIMEOUT - PROMPT_TIME;

  const [remainingMinutes, setRemainingMinutes] = useState(
    Math.ceil(countdownDuration / 1000 / 60)
  );

  const timeoutUser = useCallback(() => {
    const endAndSignOut = async () => {
      await endAppSession({ reason: 'timeout' });

      try {
        await signOut(true);
      } catch (error) {
        console.error(error);
      }

      navigate('/sign-in');
    };

    endAndSignOut().catch((error: unknown) => {
      console.error(error);
    });
  }, [navigate]);

  const onPrompt = () => {
    setIdleDialogOpen(true);
  };

  const onIdle = () => {
    timeoutUser();
  };

  const idleTimer = useIdleTimer({
    onIdle,
    onPrompt,
    promptBeforeIdle: promptTimeThreshold,
    timeout: IDLE_TIMEOUT,
    crossTab: true,
    syncTimers: 200,
    name: 'platform',
    onMessage: (data: { type: string }) => {
      // Close dialog when another tab extends the session
      if (data.type === 'idle-dialog-closed') {
        setIdleDialogOpen(false);
      }
    },
  });

  useExtendSession(idleTimer.isPrompted());

  // Update countdown timer
  useEffect(() => {
    if (!idleDialogOpen) {
      return undefined;
    }

    const updateCountdown = () => {
      const timeLeft = idleTimer.getRemainingTime();

      if (timeLeft > countdownDuration || timeLeft <= 0) {
        timeoutUser();
        return;
      }

      const minutesLeft = Math.ceil(timeLeft / 1000 / 60);
      setRemainingMinutes(minutesLeft);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [idleDialogOpen, idleTimer, countdownDuration, timeoutUser]);

  return (
    <AlertDialog
      action={() => {
        // Close this dialog and broadcast to close all other tabs' dialogs
        setIdleDialogOpen(false);
        idleTimer.message({ type: 'idle-dialog-closed' }, true);
        extendSession().catch((error) => {
          console.error(error);
        });
        Promise.resolve(onStaySignedIn?.()).catch((error: unknown) => {
          console.error(error);
        });
        idleTimer.reset();
      }}
      actionButtonText="Stay signed in"
      cancel={timeoutUser}
      cancelButtonText="Sign out"
      description={`If we don’t hear from you, we’ll sign you out in ${remainingMinutes} ${
        remainingMinutes === 1 ? 'minute' : 'minutes'
      }.`}
      icon="warning"
      open={idleDialogOpen}
      title="Stay Signed In"
      trigger={null}
    />
  );
}
